/**
 * DeliveryReportView Component
 *
 * Generates a combined delivery report (HTML) for printing/PDF that includes:
 * 1. Workshop info
 * 2. Vehicle details
 * 3. Work performed
 * 4. Inspection findings (if any)
 * 5. Checklist status (if any)
 */

import { format } from 'date-fns';
import i18n from '@/lib/i18n';
import { printHtmlInWindow, printHtmlWindow, type PrintHtmlResult } from '@/lib/printWindow';
import { useNotificationStore } from '@/stores/notificationStore';
import { conditionColors } from '@/styles/theme';
import { DEFAULT_BRANDING } from '@/config/branding';
import type { Inspection } from '@/types';
import type { BookingChecklist } from '@/hooks/useChecklists';
import type { BookingWorkPath } from '@/hooks/useWorkPaths';
import { dateFnsLocale, formatNumber } from '@/utils/locale';

interface WorkOrderSummary {
  id: string;
  workOrderNumber: string;
  vehicleRegistration: string;
  vehicleVin?: string;
  vehicleMake?: string;
  vehicleModel?: string;
  vehicleYear?: number;
  mileage?: number;
  customerName: string;
  services: { name: string; status: string }[];
  completedAt?: string;
}

interface DeliveryReportData {
  workOrder: WorkOrderSummary;
  inspection?: Inspection;
  checklists?: BookingChecklist[];
  /**
   * Work paths (#2414). Only lines flagged `showInReport` are printed: this is
   * the document the customer is handed, and the flag is what separates a
   * service finding from the workshop's own quality record (#2388).
   */
  workPaths?: BookingWorkPath[];
  workshopName?: string;
  workshopAddress?: string;
  workshopPhone?: string;
  workshopEmail?: string;
  workshopLogo?: string;
  mechanicName?: string;
}

export function getDeliveryReportHTML(data: DeliveryReportData): string {
  const t = (key: string, opts?: Record<string, unknown>) => i18n.t(key, { ns: 'workorders', ...opts });
  const ti = (key: string) => i18n.t(key, { ns: 'inspection' });
  const { workOrder, inspection, checklists, workPaths, workshopName, workshopAddress, workshopPhone, workshopEmail, mechanicName } = data;

  const dateStr = format(new Date(), 'dd.MM.yyyy', { locale: dateFnsLocale() });

  // Print color palette. Brand accents come from the centralized tokens; the
  // remaining values are fixed print ink / paper-surface neutrals that have no
  // theme token equivalent (the printed report is intentionally not
  // tenant-overridable — see styles/theme.ts conditionColors note).
  const accentColor = DEFAULT_BRANDING.colors.primary.main;
  const checkColor = conditionColors.good;
  const inkPrimary = '#333';
  const inkSecondary = '#666';
  const surfaceGrey = '#f5f5f5';
  const borderGrey = '#e0e0e0';
  const dividerFaint = '#f0f0f0';
  const issueBg = '#fff3e0';
  const measurementBg = '#e3f2fd';

  // Inspection findings HTML
  let inspectionHTML = '';
  if (inspection && inspection.categories) {
    const conditionLabels: Record<string, string> = {
      good: ti('report.good'),
      fair: ti('report.fair'),
      poor: ti('report.poor'),
      critical: ti('report.critical'),
    };
    inspectionHTML = `
      <div class="section">
        <h2>${t('deliveryReport.inspectionTitle')}</h2>
        ${inspection.categories.map((cat) => `
          <h3>${cat.name}</h3>
          <table class="findings-table">
            <thead>
              <tr>
                <th>${t('deliveryReport.item')}</th>
                <th>${t('deliveryReport.condition')}</th>
                <th>${t('deliveryReport.notes')}</th>
              </tr>
            </thead>
            <tbody>
              ${cat.items.map((item) => `
                <tr class="${item.condition === 'poor' || item.condition === 'critical' ? 'issue-row' : ''}">
                  <td>
                    <strong>${item.name}</strong>
                    ${item.measurementValues && item.measurementValues.length > 0 && item.measurementsConfig
                      ? (() => {
                          const subItems = item.subItemsConfig ?? [];
                          const measurements = item.measurementsConfig;
                          if (subItems.length === 0) {
                            return `<br><span class="measurement">${measurements.map((m: { id: string; label: string; unit: string }) => {
                              const entry = item.measurementValues?.find((v: { measurementId: string }) => v.measurementId === m.id);
                              return entry ? `${m.label}: ${entry.value} ${m.unit}` : '';
                            }).filter(Boolean).join(', ')}</span>`;
                          }
                          return `<table class="measurement-grid"><thead><tr><th></th>${measurements.map((m: { label: string; unit: string }) => `<th>${m.label} (${m.unit})</th>`).join('')}</tr></thead><tbody>${subItems.map((si: { id: string; label: string }) => `<tr><td><strong>${si.label}</strong></td>${measurements.map((m: { id: string }) => {
                            const entry = item.measurementValues?.find((v: { subItemId?: string; measurementId: string }) => v.measurementId === m.id && v.subItemId === si.id);
                            return `<td>${entry ? entry.value : '-'}</td>`;
                          }).join('')}</tr>`).join('')}</tbody></table>`;
                        })()
                      : item.hasMeasurement && item.measurementValue != null
                        ? `<br><span class="measurement">${String(item.measurementValue)} ${item.measurementUnit ?? ''}</span>`
                        : ''
                    }
                  </td>
                  <td>
                    ${item.condition
                      ? `<span class="condition-badge" style="background-color: ${conditionColors[item.condition]}20; color: ${conditionColors[item.condition]}; border: 1px solid ${conditionColors[item.condition]}; padding: 2px 8px; border-radius: 4px; font-size: 9pt;">${conditionLabels[item.condition] ?? item.condition}</span>`
                      : '-'}
                  </td>
                  <td>${item.notes ?? '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        `).join('')}
      </div>
    `;
  }

  // Checklists HTML
  let checklistsHTML = '';
  if (checklists && checklists.length > 0) {
    checklistsHTML = `
      <div class="section">
        <h2>${t('deliveryReport.checklistsTitle')}</h2>
        ${checklists.map((cl) => `
          <div class="checklist-block">
            <h3>${cl.name} <span class="checklist-status">(${cl.completedCount}/${cl.totalItems})</span></h3>
            <table class="checklist-table">
              <tbody>
                ${(cl.items as Array<{ id: string; label: string; type: string; required: boolean }>).map((item) => {
                  const entry = (cl.completedItems as Array<{ itemId: string; checked: boolean; value?: string | number; notes?: string }>)?.find((e) => e.itemId === item.id);
                  const checked = entry?.checked ?? false;
                  return `
                    <tr>
                      <td class="check-cell">${checked ? '✓' : '○'}</td>
                      <td>${item.label}</td>
                      <td class="value-cell">${entry?.value ?? ''}</td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          </div>
        `).join('')}
      </div>
    `;
  }

  // Customer-facing: internal quality lines are filtered out, and a task whose
  // every line is internal disappears rather than printing an empty heading.
  let workPathsHTML = '';
  if (workPaths && workPaths.length > 0) {
    const conditionLabel = (value: string | null | undefined) =>
      value == null || value === ''
        ? ''
        : i18n.t(`condition.${value}`, { ns: 'workpaths', defaultValue: value });

    const renderValue = (v: { type: string; value?: string | null }) => {
      if (v.value == null || v.value === '') return '';
      if (v.type === 'condition') return conditionLabel(v.value);
      if (v.type === 'checkbox') return v.value === 'true' ? '✓' : '○';
      if (v.type === 'photo') return '📷';
      return v.value;
    };

    const tasks = workPaths
      .flatMap((wp) => wp.tasks)
      .map((task) => ({
        name: task.name,
        verifications: task.verifications.filter((v) => v.showInReport !== false),
      }))
      .filter((task) => task.verifications.length > 0);

    if (tasks.length > 0) {
      workPathsHTML = `
        <div class="section">
          <h2>${t('deliveryReport.workPathsTitle')}</h2>
          ${tasks.map((task) => `
            <div class="checklist-block">
              <h3>${task.name}</h3>
              <table class="checklist-table">
                <tbody>
                  ${task.verifications.map((v) => `
                    <tr>
                      <td class="check-cell">${v.completedAt ? '✓' : '○'}</td>
                      <td>${v.label}</td>
                      <td class="value-cell">${renderValue(v)}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          `).join('')}
        </div>
      `;
    }
  }

  return `<!DOCTYPE html>
<html lang="${i18n.language}">
<head>
  <meta charset="utf-8">
  <title>${t('deliveryReport.title')} - ${workOrder.workOrderNumber}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 10pt; color: ${inkPrimary}; padding: 20mm; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 2px solid ${accentColor}; }
    .header h1 { font-size: 18pt; color: ${accentColor}; }
    .workshop-info { text-align: right; font-size: 9pt; color: ${inkSecondary}; }
    .vehicle-info { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 20px; padding: 12px; background: ${surfaceGrey}; border-radius: 6px; }
    .vehicle-info dt { font-weight: 600; font-size: 8pt; color: ${inkSecondary}; text-transform: uppercase; }
    .vehicle-info dd { font-size: 10pt; margin-bottom: 6px; }
    .section { margin-bottom: 20px; }
    .section h2 { font-size: 13pt; color: ${accentColor}; margin-bottom: 10px; padding-bottom: 5px; border-bottom: 1px solid ${borderGrey}; }
    .section h3 { font-size: 11pt; margin: 10px 0 6px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
    th, td { border: 1px solid ${borderGrey}; padding: 6px 8px; text-align: left; font-size: 9pt; }
    th { background: ${surfaceGrey}; font-weight: 600; }
    .issue-row { background: ${issueBg}; }
    .measurement { display: inline-block; background: ${measurementBg}; color: ${accentColor}; padding: 1px 6px; border-radius: 3px; font-size: 8pt; font-weight: 500; }
    .measurement-grid { width: 100%; border-collapse: collapse; font-size: 8pt; margin-top: 4px; }
    .measurement-grid th, .measurement-grid td { border: 1px solid ${borderGrey}; padding: 2px 4px; text-align: center; }
    .measurement-grid th { background: ${surfaceGrey}; font-weight: 600; }
    .measurement-grid td:first-child { text-align: left; }
    .findings-table th:first-child { width: 40%; }
    .findings-table th:nth-child(2) { width: 20%; }
    .checklist-block { margin-bottom: 12px; }
    .checklist-status { font-weight: 400; color: ${inkSecondary}; font-size: 9pt; }
    .checklist-table { border: none; }
    .checklist-table td { border: none; border-bottom: 1px solid ${dividerFaint}; padding: 3px 6px; }
    .check-cell { width: 24px; text-align: center; font-size: 12pt; }
    .value-cell { color: ${inkSecondary}; text-align: right; }
    .services-list { list-style: none; }
    .services-list li { padding: 4px 0; border-bottom: 1px solid ${dividerFaint}; }
    .services-list li:before { content: '✓ '; color: ${checkColor}; font-weight: 700; }
    .footer { margin-top: 30px; padding-top: 15px; border-top: 1px solid ${borderGrey}; display: flex; justify-content: space-between; font-size: 9pt; color: ${inkSecondary}; }
    @media print { body { padding: 10mm; } }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <h1>${t('deliveryReport.title')}</h1>
      <div>${t('deliveryReport.workOrderNumber')}: <strong>${workOrder.workOrderNumber}</strong></div>
      <div>${dateStr}</div>
    </div>
    <div class="workshop-info">
      ${workshopName ? `<strong>${workshopName}</strong><br>` : ''}
      ${workshopAddress ? `${workshopAddress}<br>` : ''}
      ${workshopPhone ? `${workshopPhone}<br>` : ''}
      ${workshopEmail ?? ''}
    </div>
  </div>

  <dl class="vehicle-info">
    <div>
      <dt>${t('deliveryReport.registration')}</dt>
      <dd>${workOrder.vehicleRegistration}</dd>
    </div>
    ${workOrder.vehicleVin ? `<div><dt>VIN</dt><dd>${workOrder.vehicleVin}</dd></div>` : ''}
    <div>
      <dt>${t('deliveryReport.vehicle')}</dt>
      <dd>${[workOrder.vehicleMake, workOrder.vehicleModel, workOrder.vehicleYear].filter(Boolean).join(' ')}</dd>
    </div>
    ${workOrder.mileage ? `<div><dt>${t('deliveryReport.mileage')}</dt><dd>${formatNumber(workOrder.mileage)} km</dd></div>` : ''}
    <div>
      <dt>${t('deliveryReport.customer')}</dt>
      <dd>${workOrder.customerName}</dd>
    </div>
  </dl>

  <div class="section">
    <h2>${t('deliveryReport.workPerformed')}</h2>
    <ul class="services-list">
      ${workOrder.services.map((s) => `<li>${s.name}</li>`).join('')}
    </ul>
  </div>

  ${inspectionHTML}
  ${workPathsHTML}
  ${checklistsHTML}

  <div class="footer">
    <div>${mechanicName ? `${t('deliveryReport.mechanic')}: ${mechanicName}` : ''}</div>
    <div>${workshopName ?? ''} — ${dateStr}</div>
  </div>
</body>
</html>`;
}

/**
 * Open the delivery report in a new window and print it. Pass `preOpenedWindow`
 * when the caller opened the window itself in the click handler (several
 * documents from one click, #4093); `null` there means the browser blocked it.
 * A blocked window is reported to the user here, so callers that ignore the
 * result still get the notification.
 */
export function generateDeliveryReport(
  data: DeliveryReportData,
  preOpenedWindow?: Window | null,
): Promise<PrintHtmlResult> {
  const html = getDeliveryReportHTML(data);
  const printing = preOpenedWindow !== undefined
    ? printHtmlInWindow(preOpenedWindow, html)
    : printHtmlWindow(html);
  return printing.then((result) => {
    if (result.status === 'popup-blocked') {
      // getState() because this is a plain function, not a component.
      useNotificationStore.getState().addNotification({
        type: 'warning',
        title: i18n.t('print.popupBlocked', { ns: 'workorders' }),
      });
    }
    return result;
  });
}
